import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import type { FilterState, Dealership, Tag, VehicleClass } from '../types';
import { CLASS_COLOURS, classLabel, PROGRESS_COLOURS, PROGRESS_STATUSES } from '../types';
import TagPill from './TagPill';

interface Props {
  filters: FilterState;
  onChange: (f: Partial<FilterState>) => void;
  onClear: () => void;
  open: boolean;
  dealerships: Dealership[];
  tags: Tag[];
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const PROGRESS_OPTIONS = PROGRESS_STATUSES.map(s => ({
  value: s,
  label: PROGRESS_COLOURS[s].label,
  bg:    PROGRESS_COLOURS[s].bg,
  text:  PROGRESS_COLOURS[s].text,
}));

const CATEGORY_GROUPS = [
  { label: 'Emergency',    items: ['Police', 'EMS', 'SWAT / Armoured Emergency', 'Fire and Rescue', 'Prison Transport / DOC'] },
  { label: 'City/Utility', items: ['Bus', 'Taxi', 'Tow Trucks', 'Vans', 'Trucks', 'Utility Vehicle', 'Construction Vehicle'] },
  { label: 'Performance',  items: ['Sports', 'Sports Classic', 'Supercar', 'Hypercar', 'Muscle', 'Drift Car'] },
  { label: 'Civil Cars',   items: ['Compact', 'Sedans', 'Hatchbacks', 'Coupes', 'Wagons', 'SUVs', 'Offroad'] },
  { label: 'Two Wheels',   items: ['Motorcycle', 'Bicycle'] },
  { label: 'Air & Water',  items: ['Airplane', 'Helicopter', 'Boat'] },
];

const TIERS = ['Tier 1', 'Tier 2', 'Tier 3'];

// ─── Atoms ────────────────────────────────────────────────────────────────────

function SLabel({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontFamily: 'Outfit', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.28)', marginBottom: 10,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {children}
      <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
    </p>
  );
}

// Generic pill (category / tier / progress)
function Pill({
  label, active, onClick,
  activeBg = '#ffffff', activeText = '#0D0D0D',
}: {
  label: string; active: boolean; onClick: () => void;
  activeBg?: string; activeText?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        fontFamily: 'Outfit', fontSize: 11, fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '0.05em',
        padding: '5px 14px',
        border: `1px solid ${active ? 'transparent' : 'rgba(255,255,255,0.11)'}`,
        background: active ? activeBg : 'rgba(255,255,255,0.03)',
        color: active ? activeText : 'rgba(255,255,255,0.38)',
        cursor: 'pointer', userSelect: 'none',
        transition: 'all 0.13s',
      }}
      onMouseEnter={e => { if (!active) { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.4)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.75)'; } }}
      onMouseLeave={e => { if (!active) { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.11)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.38)'; } }}
    >
      {label}
    </button>
  );
}

// Class sub-groups displayed as 3 vertical columns
const CLASS_GROUPS: { label: string; classes: VehicleClass[] }[] = [
  { label: 'Cars',    classes: ['S', 'A', 'B', 'C', 'D'] },
  { label: 'Special', classes: ['Police'] },
  { label: 'Bikes',   classes: ['Bike-S', 'Bike-A', 'Bike-B', 'Bike-C', 'Bike-D'] },
];

// Vertical stacked class pill with always-visible colour accent on left
function ClassPill({ vc, active, onClick }: { vc: VehicleClass; active: boolean; onClick: () => void }) {
  const col = CLASS_COLOURS[vc];
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        width: '100%', textAlign: 'left',
        padding: '6px 10px 6px 0',
        background: active ? `${col.bg}18` : 'transparent',
        border: 'none',
        borderLeft: `3px solid ${active ? col.bg : col.bg + '50'}`,
        color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
        cursor: 'pointer', userSelect: 'none',
        fontFamily: 'Outfit', fontSize: 12, fontWeight: active ? 700 : 500,
        textTransform: 'uppercase', letterSpacing: '0.07em',
        transition: 'all 0.13s',
        paddingLeft: 10,
      }}
    >
      {/* Colour swatch */}
      <span style={{
        width: 10, height: 10, flexShrink: 0,
        background: col.bg,
        opacity: active ? 1 : 0.55,
      }} />
      {classLabel(vc)}
    </button>
  );
}

// Toggle switch — fully React-driven
function Toggle({ checked, onChange, label }: {
  checked: boolean; onChange: (v: boolean) => void; label: string;
}) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onChange(!checked)}
    >
      {/* Track */}
      <div style={{
        position: 'relative', flexShrink: 0,
        width: 38, height: 21,
        background: checked ? '#C8102E' : 'rgba(255,255,255,0.08)',
        transition: 'background 0.2s',
      }}>
        {/* Dot */}
        <div style={{
          position: 'absolute', top: 3, width: 15, height: 15,
          left: checked ? 20 : 3,
          background: checked ? '#FFFFFF' : 'rgba(255,255,255,0.35)',
          transition: 'left 0.2s, background 0.2s',
        }} />
      </div>
      <span style={{
        fontFamily: 'Inter', fontSize: 12,
        color: checked ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.38)',
        transition: 'color 0.15s',
      }}>
        {label}
      </span>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function FilterPanel({ filters, onChange, onClear, open, dealerships, tags }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!panelRef.current) return;
    if (open) {
      gsap.fromTo(panelRef.current,
        { height: 0, opacity: 0 },
        { height: 'auto', opacity: 1, duration: 0.28, ease: 'power3.out' },
      );
    } else {
      gsap.to(panelRef.current, { height: 0, opacity: 0, duration: 0.2, ease: 'power2.in' });
    }
  }, [open]);

  const toggleMulti = (key: keyof FilterState, val: string) => {
    const cur = filters[key] as string[];
    onChange({ [key]: cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val] });
  };

  const colStyle: React.CSSProperties = {
    padding: '18px 22px',
    borderRight: '1px solid rgba(255,255,255,0.05)',
  };

  return (
    <div ref={panelRef} className="overflow-hidden" style={{ height: 0 }}>
      <div style={{
        background: '#111111',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
      }}>

        {/* ══ COL 1 — CATEGORIES ══════════════════════════════════ */}
        <div style={colStyle}>
          <SLabel>Category</SLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {CATEGORY_GROUPS.map(group => (
              <div key={group.label}>
                {/* Group header — label + full-width rule */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                  <span style={{
                    fontFamily: 'Outfit', fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.14em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.22)', flexShrink: 0,
                  }}>
                    {group.label}
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
                </div>
                {/* Pills — full width, more padding */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {group.items.map(cat => (
                    <Pill key={cat} label={cat}
                      active={filters.category.includes(cat)}
                      onClick={() => toggleMulti('category', cat)}
                      activeBg="#FFFFFF" activeText="#0D0D0D"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ COL 2 — CLASS · TIER · PROGRESS ════════════════════ */}
        <div style={{ ...colStyle, display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Vehicle Class — 3 vertical sub-groups */}
          <div>
            <SLabel>Vehicle Class</SLabel>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0 16px' }}>
              {CLASS_GROUPS.map((group, gi) => (
                <div key={group.label}>
                  {/* Sub-group label */}
                  <p style={{
                    fontFamily: 'Outfit', fontSize: 9, fontWeight: 700,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.18)', marginBottom: 6,
                  }}>
                    {group.label}
                  </p>
                  {/* Stacked pills */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {group.classes.map(vc => (
                      <ClassPill key={vc} vc={vc}
                        active={filters.vehicle_class.includes(vc)}
                        onClick={() => toggleMulti('vehicle_class', vc)}
                      />
                    ))}
                  </div>
                  {/* Vertical divider between groups */}
                  {gi < CLASS_GROUPS.length - 1 && (
                    <div style={{ position: 'absolute' }} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tier */}
          <div>
            <SLabel>Tier</SLabel>
            <div style={{ display: 'flex', gap: 5 }}>
              {TIERS.map(t => (
                <Pill key={t} label={t}
                  active={filters.tier.includes(t)}
                  onClick={() => toggleMulti('tier', t)}
                  activeBg="#FFFFFF" activeText="#0D0D0D"
                />
              ))}
            </div>
          </div>

          {/* Progress */}
          <div>
            <SLabel>Progress Status</SLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {PROGRESS_OPTIONS.map(s => (
                <Pill key={s.value} label={s.label}
                  active={filters.progress.includes(s.value)}
                  onClick={() => toggleMulti('progress', s.value)}
                  activeBg={s.bg} activeText={s.text}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ══ COL 3 — SPECS · DEALERSHIP · FLAGS ══════════════════ */}
        <div style={{ ...colStyle, borderRight: 'none', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Ranges */}
          {([
            { label: 'Doors',     minK: 'min_doors' as const, maxK: 'max_doors' as const },
            { label: 'Seats',     minK: 'min_seats' as const, maxK: 'max_seats' as const },
            { label: 'Top Speed', minK: 'min_speed' as const, maxK: 'max_speed' as const },
          ]).map(({ label, minK, maxK }) => (
            <div key={label}>
              <SLabel>{label}</SLabel>
              <div style={{ display: 'flex', gap: 6 }}>
                <input className="filter-num" type="number" placeholder="Min"
                  value={filters[minK]} onChange={e => onChange({ [minK]: e.target.value })} />
                <input className="filter-num" type="number" placeholder="Max"
                  value={filters[maxK]} onChange={e => onChange({ [maxK]: e.target.value })} />
              </div>
            </div>
          ))}

          {/* Dealership */}
          <div>
            <SLabel>Dealership</SLabel>
            <div className="filter-select-wrap">
              <select className="filter-select" value={filters.dealership_id}
                onChange={e => onChange({ dealership_id: e.target.value })}>
                <option value="">All Dealerships</option>
                {dealerships.map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
              </select>
            </div>
          </div>

          {/* Flags */}
          <div>
            <SLabel>Flags</SLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
              <Toggle checked={filters.dealership_available} onChange={v => onChange({ dealership_available: v })} label="Has Dealership" />
              <Toggle checked={filters.boosting_exclusive}   onChange={v => onChange({ boosting_exclusive: v })}   label="Boosting Exclusive" />
              <Toggle checked={filters.rp_exclusive}         onChange={v => onChange({ rp_exclusive: v })}         label="RP Exclusive" />
            </div>
          </div>

          {/* Tags */}
          {tags.length > 0 && (
            <div>
              <SLabel>Release Tags</SLabel>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                {tags.map(t => {
                  const active = filters.tag_ids.includes(String(t.id));
                  return (
                    <button key={t.id} type="button"
                      onClick={() => toggleMulti('tag_ids', String(t.id))}
                      style={{ opacity: active ? 1 : 0.28, transition: 'opacity .15s' }}>
                      <TagPill tag={t} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Clear */}
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClear}
              style={{
                fontFamily: 'Outfit', fontSize: 11, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.1em',
                padding: '7px 18px',
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'transparent',
                color: 'rgba(255,255,255,0.32)',
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { const b = e.currentTarget; b.style.borderColor = '#C8102E'; b.style.color = '#C8102E'; }}
              onMouseLeave={e => { const b = e.currentTarget; b.style.borderColor = 'rgba(255,255,255,0.15)'; b.style.color = 'rgba(255,255,255,0.32)'; }}
            >
              Clear All
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
